import { FunctionComponent, useEffect, useState } from "react";
import {
  selectPlanItemsWithLocations,
  selectPlanView,
  selectStartingPoints,
} from "@store/plan/planReducer";
import { useAppSelector, useAppDispatch } from "@store/store";
import { useMap } from "@vis.gl/react-google-maps";
import { selectMenuItem } from "@store/menuItem/menuItemReducer";
import {
  setDirections,
  selectTravelMode,
  selectNavigationDirectionIndex,
} from "@store/planRouteReducer";
import { useWindowSize } from "@hooks/useWindowSize/useWindowSize";
import { getDirections } from "@lib/maps/directions";
import { logError } from "@lib/logger";
import { RoutePolyline } from "../elements/RoutePolyline";
import { useMatch } from "react-router";
import { getPolylinePath } from "./getPolylinePath";
import { PlanMarkers } from "./PlanMarkers";

import "./PlanRoute.css";

/**
 * Show the plan route and markers, including for navigation where pieces of the route are shown
 * individually.
 */
const PlanRoute: FunctionComponent = () => {
  const map = useMap();
  const dispatch = useAppDispatch();
  const planItems = useAppSelector(selectPlanItemsWithLocations);
  const menuItem = useAppSelector(selectMenuItem);
  const travelMode = useAppSelector(selectTravelMode);
  const navigationDirectionIndex = useAppSelector(
    selectNavigationDirectionIndex,
  );
  const planView = useAppSelector(selectPlanView);
  const { isMobile } = useWindowSize();
  const [polylinePath, setPolylinePath] = useState<
    { lat: number; lng: number }[]
  >([]);
  const [bounds, setBounds] = useState<google.maps.LatLngBounds | null>(null);
  const isPlanScreen = useMatch("/plan");
  const startingPoints = useAppSelector(selectStartingPoints);

  useEffect(() => {
    if (!map || !isPlanScreen) {
      return;
    }

    const fetchRoute = async () => {
      try {
        const result = await getDirections(
          planItems,
          startingPoints,
          travelMode,
          false,
        );

        dispatch(setDirections(result));

        const { polylinePath, bounds } = getPolylinePath(
          result,
          planView,
          navigationDirectionIndex,
        );

        setPolylinePath(polylinePath);
        setBounds(bounds);
      } catch (error) {
        logError(error);
      }
    };

    void fetchRoute();
  }, [
    planItems,
    map,
    menuItem,
    dispatch,
    isMobile,
    travelMode,
    planView,
    navigationDirectionIndex,
    isPlanScreen,
    startingPoints,
  ]);

  if (!isPlanScreen || planView == "edit") {
    return null;
  }

  return (
    <>
      <PlanMarkers />
      <RoutePolyline
        bounds={bounds}
        path={polylinePath}
        travelMode={travelMode}
      />
    </>
  );
};

export { PlanRoute };
